import type { StateTree } from '@makemydeal/dr-dash-types';
import type {
    CustomizablePdfBalloonBreakdown,
    CustomizablePdfCashBreakdown,
    CustomizablePdfDeal,
    CustomizablePdfFinanceBreakdown,
    CustomizablePdfLeaseBreakdown,
    CustomizablePdfPayload,
    CustomizablePdfPaymentBreakdown,
    DetailedBreakdownItem,
    GeneratePdfPayload,
    OfferType,
    PdfDealer,
    PdfDealTermGridData,
    PdfPerson,
    PdfTradeIn,
    PdfVehicle
} from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import * as accessoriesSelectors from './accessoriesSelectors';
import * as calculationDetailsSelectors from './calculationDetailsSelectors';
import * as compositeSelectors from './compositeSelectors';
import * as configSelectors from './config';
import * as offerReduxSelectors from './offerRedux';
import * as priorBalanceSelectors from './priorBalanceSelectors';
import { getPrintingLogoUrl } from './globalPrintingSettingsSelectors';
import * as quoteSelectors from './quoteSelectors';
import { getBalloonAmount, getBalloonTermByType, getCreditDecisionLender } from './offerRedux';
import * as offerSelectors from './offerSelectors';
import { capitalizeOfferType, getScopePaymentSummary, isFlexBuyFinance } from './scopePaymentSummary';
import * as vehicleProtectionSelectors from './vehicleProtectionSelectors';
import * as vehicleSelectors from './vehicleSelectors';
import * as widgetSettingsSelectors from './widgetSettingsSelectors';
import * as tradeInsSelectors from './tradeIns';
import * as employeeDetailsSelectors from './employeeDetailsSelectors';
import { getFuelSavingsPrintData } from './fuelSavingsPrintSelectors';
import { getAutoLeadId } from './dealXgDetailsSelectors';
import { getFullName as getBridgeUserFullName } from './bridgeUserSelectors';

import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { ScopeGridSelections } from '../types/types';
import { getSummaryDealerDiscountsForPrinting } from './dealerDiscountsSelectors';
import { UNLIMITED_MILEAGE } from '@makemydeal/dr-platform-shared';
import { formatNumber } from '@makemydeal/dr-common-utils';
import type { TradeInState } from '../types/tradeInTypes';
import { getFuelSavingPdfData } from './fuelSavingPdfSelectors';

const getMvPaymentSpread = (state: StateTree, offerType: OfferType): number => {
    return compositeSelectors.getMvPaymentRangeByOfferType(state, offerType);
};

const {
    getAccessoriesBreakdown,
    getCustomizableTaxesAndFeesBreakdown,
    getMultiTradeInEquityBreakdown,
    getVehicleProtectionBreakdown,
    getEstDueAtSigningBreakdown,
    getTaxesBreakdown,
    getGovernmentFeesBreakdown,
    getDealerFeesBreakdown,
    getFeesTotal,
    getRebatesBreakdown
} = offerReduxUtils;

export const isUnlimitedMileage = (mileage?: number) => !mileage || mileage <= 0 || mileage >= 999_999;

/**
 * Transforms a numeric value into the DetailedBreakdown structure format without items.
 * @param value - The numeric value to transform
 * @returns An object with the value property, or undefined if value is falsy
 */
const toDetailedBreakdownValue = (value?: number): { value: number } | undefined => {
    return value ? { value } : undefined;
};

const omitTotalOfPaymentsAndConsideration = (gridData?: PdfDealTermGridData): PdfDealTermGridData | undefined =>
    gridData?.map((row) => row.map((cell) => cell && { ...cell, totalOfPaymentsAndConsideration: undefined }));

const getDealLink = (state: StateTree) => {
    const _bffConfig = configSelectors.getServicesBff(state);
    const _urlConfig = configSelectors.getUrls(state);
    const dealXgId = offerReduxSelectors.getDealXgId(state);
    const prefix = _urlConfig?.sendToShopper || `${_bffConfig.protocol}://${_bffConfig.host}`;
    const isCustomerViewEnabled = dealerSelectors.isCustomerViewExperience(state);
    const queryParam = isCustomerViewEnabled ? '&isCustomerView=true' : '&route=shopper-quote-dashboard';
    return `${prefix}/shopperPage.html?dealXgId=${dealXgId}${queryParam}`;
};

/**
 * Returns warranty details string for VSC products in the format:
 * "*Payments include Packages: 1) Product Name Term, Mileage Deductible, 2) Product Name Term, Mileage Deductible, ..."
 */
export const getVscWarrantyDetails = (state: StateTree): string | undefined => {
    const selectedVppProducts = vehicleProtectionSelectors.getVppSelectedProducts(state);
    const vscProducts = selectedVppProducts.filter((product) => product.productCategoryCode === 'VSC');

    if (vscProducts.length === 0) {
        return undefined;
    }

    const warrantyEntries = vscProducts.map((product, index) => {
        const productName = product.productName || 'VSC Product';
        const termYears = product.productCoverageLength ? formatNumber(product.productCoverageLength / 12) : 0;
        const mileageK = product.productCoverageMiles ? formatNumber(product.productCoverageMiles / 1000) : 0;
        const deductible = product.productDeductible || 0;

        const termDisplay = `${termYears} YR`;

        const mileageDisplay = isUnlimitedMileage(product.productCoverageMiles ?? 0) ? UNLIMITED_MILEAGE : `${mileageK}K mi`;
        const deductibleDisplay = `$${deductible.toLocaleString()}`;

        const details = [termDisplay, mileageDisplay, deductibleDisplay].filter(Boolean).join(', ');

        return `${index + 1}) ${productName} ${details}`;
    });

    return `*Payments include Packages: ${warrantyEntries.join(', ')}`;
};

/** CUSTOMIZABLE PDF PAYLOAD **/

const getPdfVehicle = (state: StateTree): PdfVehicle => {
    return {
        condition: vehicleSelectors.getVehicleCondition(state) as PdfVehicle['condition'],
        make: vehicleSelectors.getVehicleMake(state),
        model: vehicleSelectors.getVehicleModel(state),
        trim: vehicleSelectors.getVehicleTrim(state),
        vin: vehicleSelectors.getVehicleVin(state),
        stockNumber: vehicleSelectors.getVehicleStockNumber(state),
        urls: { imageUrl: vehicleSelectors.getVehicleImageUrl(state) },
        color: vehicleSelectors.getVehicleExteriorColor(state),
        engine: vehicleSelectors.getVehicleEngineSize(state),
        fuelEconomy: vehicleSelectors.getFuelEconomyValue(state),
        retailPrice: offerSelectors.getRetailPriceOverrideWithFallback(state),
        mileage: vehicleSelectors.getMileage(state),
        year: vehicleSelectors.getVehicleYear(state)
    };
};

const getPdfDealer = (state: StateTree): PdfDealer => {
    return {
        id: dealerSelectors.getDealerId(state),
        name: dealerSelectors.getDealerName(state),
        brandingLogoUrl: dealerSelectors.getDealerBrandingExperienceLogo(state),
        street: dealerSelectors.getDealerStreet(state),
        city: dealerSelectors.getDealerCity(state),
        state: dealerSelectors.getDealerStateCode(state),
        zip: dealerSelectors.getDealerZip(state),
        timeZoneId: dealerSelectors.getDealerTimeZoneId(state),
        sponsor: widgetSettingsSelectors.getSponsor(state),
        salesPerson: {
            firstName: employeeDetailsSelectors.getPrimarySalespersonFirstName(state),
            lastName: employeeDetailsSelectors.getPrimarySalespersonLastName(state)
        },
        phoneNumber: dealerSelectors.getDealerPhone(state)
    } as PdfDealer;
};

const getPdfShopper = (state: StateTree): PdfPerson => {
    const shopperInfo = offerReduxSelectors.getShopperInfo(state);

    return {
        firstName: shopperInfo.firstName,
        lastName: shopperInfo.lastName,
        customerType: shopperInfo.customerType,
        business: shopperInfo.business,
        phone: shopperInfo.phone,
        email: shopperInfo.email,
        streetAddress: shopperInfo.streetAddress,
        streetAddress2: shopperInfo.streetAddress2,
        city: shopperInfo.city,
        state: shopperInfo.state,
        country: shopperInfo.country,
        zip: shopperInfo.zip
    };
};

const getCoBuyerName = (state: StateTree): string | undefined => {
    if (!offerReduxSelectors.hasCoBuyer(state)) return undefined;

    const coBuyer = offerReduxSelectors.getCoBuyerInfo(state);
    if (!coBuyer) return undefined;

    const name = `${coBuyer.firstName || ''} ${coBuyer.lastName || ''}`.trim();
    return name || undefined;
};

const isTradeInEmpty = (tradeIn: PdfTradeIn): boolean => {
    return (
        !tradeIn.year &&
        !tradeIn.make &&
        !tradeIn.model &&
        !tradeIn.reconditionCost &&
        !tradeIn.trim &&
        !tradeIn.value &&
        !tradeIn.vin &&
        !tradeIn.mileage &&
        !tradeIn.payoff
    );
};

function mapTradeInToPdfTradeIn(tradeIn: TradeInState): PdfTradeIn {
    return {
        year: (tradeIn.year || '').toString(),
        make: tradeIn.make || '',
        model: tradeIn.model || '',
        trim: tradeIn.trim || '',
        value: tradeIn.value || 0,
        vin: tradeIn.vin?.toUpperCase() || '',
        mileage: tradeIn.mileage || 0,
        reconditionCost: tradeIn.tradeReconditionDetails?.tradeReconditionTotal || 0,
        payoff: tradeIn.amountOwed || 0
    };
}

const getPdfTradeIns = (state: StateTree): PdfTradeIn[] => {
    const tradeIns = tradeInsSelectors.getIncludedTradeIns(state) ?? [];
    return tradeIns.map(({ tradeIn }) => mapTradeInToPdfTradeIn(tradeIn)).filter((trade) => !isTradeInEmpty(trade));
};

const getPdfDealTradesMap = (state: StateTree) => {
    const includedTradeIns = tradeInsSelectors.getIncludedTradeInsWithOriginalIndexes(state);

    return includedTradeIns.reduce((map, { tradeIn, originalIndex }) => {
        const mappedTradeIn = mapTradeInToPdfTradeIn(tradeIn);

        if (isTradeInEmpty(mappedTradeIn)) {
            return map;
        }

        map[`trade${originalIndex}`] = mappedTradeIn;
        return map;
    }, {} as Record<string, PdfTradeIn>);
};

/** CUSTOMIZABLE PDF PAYLOAD **/

/**
 * Returns the total addon for finance and cash.
 * Total Add-ons = Accessories, Protection Products, Fees (Dealer & Govt)
 */
export const getTotalAddons = (state: StateTree): number => {
    const protectionTotal = vehicleProtectionSelectors.getVppSummary(state).totalProductPrice || 0;
    const accessoriesTotal = accessoriesSelectors.getAccessoriesTotalAmount(state);
    const govFeesTotal = offerSelectors.getDmvFeeTotal(state);
    const dealerFeeTotal = offerSelectors.getDealerFeeTotal(state);

    return govFeesTotal + accessoriesTotal + protectionTotal + dealerFeeTotal;
};

export const getCustomizablePdfPaymentFinance = (
    state: StateTree,
    offerType: typeof FINANCE | typeof BALLOON = FINANCE
): CustomizablePdfFinanceBreakdown => {
    const isFordFlexBuyDeal = offerReduxSelectors.isFordFlexBuyDeal(state);
    const sellingPrice = offerReduxSelectors.getOfferPrice(state) ?? 0;
    const warrantyDetails = getVscWarrantyDetails(state);
    const shouldIncludeTrade = tradeInsSelectors.getIncludedTradeIns(state)?.length > 0;
    const rebatesTotal = getRebatesBreakdown(state, offerType);
    const discount = getSummaryDealerDiscountsForPrinting(state);
    // Not routed through `toDetailedBreakdownValue`: that helper's truthy check drops a real 0,
    // which would make the print payload disagree with the on-screen $0.00 render.
    const isCarsActDisclosuresApplicable = dealerSelectors.isCarsActDisclosuresApplicable(state);
    const totalOfPaymentsAndConsiderationValue = isCarsActDisclosuresApplicable
        ? offerReduxSelectors.getTotalOfPaymentsAndConsideration(state, offerType)
        : undefined;

    return {
        // itemized
        accessoriesTotal: getAccessoriesBreakdown(state),
        ...(totalOfPaymentsAndConsiderationValue !== undefined && {
            totalOfPaymentsAndConsideration: { value: totalOfPaymentsAndConsiderationValue }
        }),
        ...(shouldIncludeTrade && {
            netTradeInEquity: getMultiTradeInEquityBreakdown(state),
            tradePayoff: toDetailedBreakdownValue(tradeInsSelectors.getIncludedTradeInsTotalAmountOwed(state)),
            tradeTaxSavings: toDetailedBreakdownValue(compositeSelectors.getTotalTradeInTaxReduction(state))
        }),
        taxesAndFeesTotal: getCustomizableTaxesAndFeesBreakdown(state, offerType, true),
        vehicleProtectionTotal: getVehicleProtectionBreakdown(state, true),
        rebates: rebatesTotal,
        totalTaxes: getTaxesBreakdown(state, offerType),
        governmentFees: getGovernmentFeesBreakdown(state),
        prodDocFees: getDealerFeesBreakdown(state, offerType),
        discount,

        // header
        monthlyPayment: toDetailedBreakdownValue(offerReduxSelectors.getMonthlyPaymentByOfferType(state, offerType) || undefined),
        term: toDetailedBreakdownValue(offerReduxSelectors.getSelectedTermMonths(state) as number),
        apr: toDetailedBreakdownValue(offerReduxSelectors.getSellRateOverride(state, true)),
        paymentRange: toDetailedBreakdownValue(getMvPaymentSpread(state, offerType)),

        // fields values
        msrp: toDetailedBreakdownValue(offerReduxSelectors.getRetailPriceOverrideByOfferType(state, offerType, true)),
        sellingPrice: toDetailedBreakdownValue(sellingPrice),
        estimatedTotalFinanced: toDetailedBreakdownValue(offerReduxSelectors.getSelectedTermAmountFinanced(state)),
        flexBuyData: {
            ...offerReduxSelectors.getFordFlexBuyData(state, ['specialPaymentFormula']),
            ...(isFordFlexBuyDeal && {
                ...(offerReduxSelectors.getCurrentFlexBuyPayments(state) ?? offerReduxSelectors.getFordFlexBuyData(state))
            })
        },
        totalSavings: toDetailedBreakdownValue((discount?.value ?? 0) + (rebatesTotal.value ?? 0)),
        securityDeposit: toDetailedBreakdownValue(offerReduxSelectors.getSecurityDepositOverride(state, true)),
        grossCapCost: toDetailedBreakdownValue(offerReduxSelectors.getCapCostTotal(state)),
        cashDown: toDetailedBreakdownValue(offerReduxSelectors.getDownPaymentByOfferType(state, offerType)),
        paidByCustomer: toDetailedBreakdownValue(offerReduxSelectors.getCashDueFromCustomer(state)),
        acquisitionFee: toDetailedBreakdownValue(offerReduxSelectors.getDealerAcquisitionFeeAmountByOfferType(state, offerType)),
        grossTaxes: toDetailedBreakdownValue(
            getTaxesBreakdown(state, offerType).value ?? offerReduxSelectors.getTotalTaxReduction(state) ?? 0
        ),
        totalFees: toDetailedBreakdownValue(getFeesTotal(state, offerType)),
        addOns: toDetailedBreakdownValue(getTotalAddons(state)),
        subtotal: toDetailedBreakdownValue(sellingPrice + getTotalAddons(state)),
        priorBalance: toDetailedBreakdownValue(priorBalanceSelectors.getPriorBalanceAmountForPrint(state, offerType)),
        warrantyDetails
    };
};

export const getCustomizablePdfPaymentBalloon = (state: StateTree): CustomizablePdfBalloonBreakdown => {
    return {
        ...getCustomizablePdfPaymentFinance(state, BALLOON),
        balloonAmount: toDetailedBreakdownValue(getBalloonAmount(state)),
        balloonTerm: toDetailedBreakdownValue(getBalloonTermByType(state)),
        lenderLegalName: getCreditDecisionLender(state)?.name
    };
};

export const getCustomizablePdfPaymentLease = (state: StateTree): CustomizablePdfLeaseBreakdown => {
    const sellingPrice = offerReduxSelectors.getOfferPrice(state) ?? 0;

    const warrantyDetails = getVscWarrantyDetails(state);
    const shouldIncludeTrade = tradeInsSelectors.getIncludedTradeIns(state)?.length > 0;
    const isOnePayLeaseDeal = offerReduxSelectors.isOnePayLease(state);
    const rebatesTotal = getRebatesBreakdown(state, LEASE);
    const discount = getSummaryDealerDiscountsForPrinting(state);
    // Not routed through `toDetailedBreakdownValue`: that helper's truthy check drops a real 0,
    // which would make the print payload disagree with the on-screen $0.00 render.
    const isCarsActDisclosuresApplicable = dealerSelectors.isCarsActDisclosuresApplicable(state);
    const totalOfPaymentsAndConsiderationValue = isCarsActDisclosuresApplicable
        ? offerReduxSelectors.getTotalOfPaymentsAndConsideration(state, LEASE)
        : undefined;

    return {
        // itemized
        accessoriesTotal: getAccessoriesBreakdown(state),
        ...(totalOfPaymentsAndConsiderationValue !== undefined && {
            totalOfPaymentsAndConsideration: { value: totalOfPaymentsAndConsiderationValue }
        }),
        estDueAtSigning: getEstDueAtSigningBreakdown(
            state,
            LEASE,
            offerReduxSelectors.getFirstPaymentCapitalized(state) ?? calculationDetailsSelectors.getIsFirstPaymentCapitalized(state)
        ),
        rebates: rebatesTotal,
        ...(shouldIncludeTrade && {
            netTradeInEquity: getMultiTradeInEquityBreakdown(state),
            tradePayoff: toDetailedBreakdownValue(tradeInsSelectors.getIncludedTradeInsTotalAmountOwed(state)),
            tradeTaxSavings: toDetailedBreakdownValue(compositeSelectors.getTotalTradeInTaxReduction(state))
        }),
        taxesAndFeesTotal: getCustomizableTaxesAndFeesBreakdown(state, LEASE, true),
        vehicleProtectionTotal: getVehicleProtectionBreakdown(state, true),
        totalTaxes: getTaxesBreakdown(state, LEASE),
        governmentFees: getGovernmentFeesBreakdown(state),
        prodDocFees: getDealerFeesBreakdown(state, LEASE),
        discount,

        // pdf header
        monthlyPayment: toDetailedBreakdownValue(offerReduxSelectors.getMonthlyPaymentByOfferType(state, LEASE) || undefined),
        term: toDetailedBreakdownValue(offerReduxSelectors.getSelectedTermMonths(state) as number),
        mileage: toDetailedBreakdownValue(offerReduxSelectors.getAnnualMiles(state)),
        paymentRange: toDetailedBreakdownValue(getMvPaymentSpread(state, LEASE)),

        // fields values
        msrp: toDetailedBreakdownValue(offerReduxSelectors.getRetailPriceOverrideByOfferType(state, LEASE, true)),
        estimatedAdjustedCapCost: toDetailedBreakdownValue(offerReduxSelectors.getAdjustedCapCost(state)),
        residualValue: toDetailedBreakdownValue(offerReduxSelectors.getAdjustedResidualValue(state)),
        sellingPrice: toDetailedBreakdownValue(sellingPrice),
        totalSavings: toDetailedBreakdownValue((discount?.value ?? 0) + (rebatesTotal.value ?? 0)),
        additionalCapItems: toDetailedBreakdownValue(offerReduxSelectors.getAddsToCapCostTotal(state)),
        securityDeposit: toDetailedBreakdownValue(offerReduxSelectors.getSecurityDepositOverride(state, true)),
        grossCapCost: toDetailedBreakdownValue(offerReduxSelectors.getCapCostTotal(state)),
        cashCapReduction: toDetailedBreakdownValue(
            offerReduxSelectors.getCapCostReduction(state, LEASE)
                ? Number(offerReduxSelectors.getCapCostReduction(state, LEASE))
                : 0
        ),
        cashDown: toDetailedBreakdownValue(offerReduxSelectors.getDownPaymentByOfferType(state, LEASE)),
        paidByCustomer: toDetailedBreakdownValue(offerReduxSelectors.getCashDueFromCustomer(state)),
        apr: toDetailedBreakdownValue(offerReduxSelectors.getSellRateOverride(state, true)),
        acquisitionFee: toDetailedBreakdownValue(offerReduxSelectors.getDealerAcquisitionFeeAmountByOfferType(state, LEASE)),
        capitalizedTaxes: toDetailedBreakdownValue(offerReduxSelectors.getCapitalizedTaxTotal(state)),
        grossTaxes: toDetailedBreakdownValue(
            getTaxesBreakdown(state, LEASE).value ?? offerReduxSelectors.getTotalTaxReduction(state) ?? 0
        ),
        totalFees: toDetailedBreakdownValue(getFeesTotal(state, LEASE)),
        priorBalance: toDetailedBreakdownValue(priorBalanceSelectors.getPriorBalanceAmountForPrint(state, LEASE)),
        warrantyDetails,
        isSinglePayLease: isOnePayLeaseDeal,
        rateType: offerReduxSelectors.getRateType(state) || 'APR'
    };
};

export const getCustomizablePdfPaymentCash = (state: StateTree): CustomizablePdfCashBreakdown => {
    const sellingPrice = offerReduxSelectors.getOfferPrice(state) ?? 0;
    const warrantyDetails = getVscWarrantyDetails(state);
    const shouldIncludeTrade = tradeInsSelectors.getIncludedTradeIns(state)?.length > 0;
    const totalAmountDue = offerReduxSelectors.getCashTotalAmountDueAfterCredits(state) ?? 0;
    const rebatesTotal = getRebatesBreakdown(state, CASH);
    const cashDown = offerReduxSelectors.getDealDeposit(state);
    const isCashDownPaymentFieldEnabled = featureToggleSelectors.isCashDownPaymentFieldEnabled(state);
    // When enableCashDownPaymentField is ON, totalAmountDue is already net of cashDown (Payment Services subtracts holdDeposit)
    const balanceDue = isCashDownPaymentFieldEnabled ? totalAmountDue : totalAmountDue - (cashDown ?? 0);
    const discount = getSummaryDealerDiscountsForPrinting(state);

    return {
        // itemized
        accessoriesTotal: getAccessoriesBreakdown(state),
        ...(shouldIncludeTrade && {
            netTradeInEquity: getMultiTradeInEquityBreakdown(state),
            tradePayoff: toDetailedBreakdownValue(tradeInsSelectors.getIncludedTradeInsTotalAmountOwed(state)),
            tradeTaxSavings: toDetailedBreakdownValue(compositeSelectors.getTotalTradeInTaxReduction(state))
        }),
        taxesAndFeesTotal: getCustomizableTaxesAndFeesBreakdown(state, CASH, true),
        rebates: rebatesTotal,
        vehicleProtectionTotal: getVehicleProtectionBreakdown(state, true),
        totalTaxes: getTaxesBreakdown(state, CASH),
        governmentFees: getGovernmentFeesBreakdown(state),
        prodDocFees: getDealerFeesBreakdown(state, CASH),
        discount,

        // Converted to DetailedBreakdown structure format
        msrp: toDetailedBreakdownValue(offerReduxSelectors.getRetailPriceOverrideByOfferType(state, CASH, true)),
        sellingPrice: toDetailedBreakdownValue(sellingPrice),
        totalSavings: toDetailedBreakdownValue((discount?.value ?? 0) + (rebatesTotal.value ?? 0)),
        totalAmountDue: toDetailedBreakdownValue(totalAmountDue),
        securityDeposit: toDetailedBreakdownValue(offerReduxSelectors.getSecurityDepositOverride(state, true)),
        grossCapCost: toDetailedBreakdownValue(offerReduxSelectors.getCapCostTotal(state)),
        cashDown: toDetailedBreakdownValue(cashDown),
        paidByCustomer: toDetailedBreakdownValue(offerReduxSelectors.getCashDueFromCustomer(state)),
        grossTaxes: toDetailedBreakdownValue(
            getTaxesBreakdown(state, CASH).value + (offerReduxSelectors.getTotalTaxReduction(state) ?? 0)
        ),
        totalFees: toDetailedBreakdownValue(getFeesTotal(state, CASH)),
        addOns: toDetailedBreakdownValue(getTotalAddons(state)),
        subtotal: toDetailedBreakdownValue(sellingPrice + getTotalAddons(state)),
        warrantyDetails,
        balanceDue: toDetailedBreakdownValue(balanceDue)
    };
};

const createMapBreakdownItemsWithDefaultName =
    (prefix: string) =>
    (item: DetailedBreakdownItem, index: number): DetailedBreakdownItem => {
        return {
            ...item,
            name: item.name || `${prefix} ${index + 1}`
        };
    };

const defaultOptionalDeskingFields = (
    breakdown: CustomizablePdfFinanceBreakdown | CustomizablePdfLeaseBreakdown | CustomizablePdfCashBreakdown
): CustomizablePdfFinanceBreakdown | CustomizablePdfLeaseBreakdown | CustomizablePdfCashBreakdown => {
    const accessoriesTotal = breakdown.accessoriesTotal
        ? {
              ...breakdown.accessoriesTotal,
              items: (breakdown.accessoriesTotal.items ?? []).map(createMapBreakdownItemsWithDefaultName('Accessory'))
          }
        : undefined;

    const rebates = breakdown.rebates
        ? {
              ...breakdown.rebates,
              items: (breakdown.rebates.items ?? []).map(createMapBreakdownItemsWithDefaultName('Incentive'))
          }
        : undefined;

    const governmentFees = breakdown.governmentFees
        ? {
              ...breakdown.governmentFees,
              items: (breakdown.governmentFees.items ?? []).map(createMapBreakdownItemsWithDefaultName('Fee'))
          }
        : undefined;

    const prodDocFees = breakdown.prodDocFees
        ? {
              ...breakdown.prodDocFees,
              items: (breakdown.prodDocFees.items ?? []).map(createMapBreakdownItemsWithDefaultName('Fee'))
          }
        : undefined;

    return {
        ...breakdown,
        accessoriesTotal,
        rebates,
        governmentFees,
        prodDocFees
    };
};

export const getCustomizablePdfPaymentPayload = (state: StateTree, offerType: OfferType): CustomizablePdfPaymentBreakdown => {
    let pdfPaymentBreakdown: CustomizablePdfPaymentBreakdown;

    switch (offerType) {
        case FINANCE: {
            const financeBreakdown = getCustomizablePdfPaymentFinance(state);

            pdfPaymentBreakdown = {
                offerType,
                finance: defaultOptionalDeskingFields(financeBreakdown) as CustomizablePdfFinanceBreakdown
            };

            break;
        }
        case LEASE: {
            const leaseBreakdown = getCustomizablePdfPaymentLease(state);

            pdfPaymentBreakdown = {
                offerType,
                lease: defaultOptionalDeskingFields(leaseBreakdown) as CustomizablePdfLeaseBreakdown
            };

            break;
        }
        case CASH: {
            const cashBreakdown = getCustomizablePdfPaymentCash(state);

            pdfPaymentBreakdown = {
                offerType,
                cash: defaultOptionalDeskingFields(cashBreakdown) as CustomizablePdfCashBreakdown
            };

            break;
        }
        case BALLOON: {
            const balloonBreakdown = getCustomizablePdfPaymentBalloon(state);

            pdfPaymentBreakdown = {
                offerType,
                balloon: defaultOptionalDeskingFields(balloonBreakdown) as CustomizablePdfBalloonBreakdown
            };

            break;
        }
    }

    return pdfPaymentBreakdown;
};

type CustomizablePdfDealWithFuelSavings = CustomizablePdfDeal;

export const getDealForCustomizablePdf = (
    state: StateTree,
    isPrimaryScenario: boolean,
    primaryState?: StateTree,
    gridSelection?: { selectedTermIndices: number[]; selectedDownPaymentIndices: number[] }
): CustomizablePdfDealWithFuelSavings => {
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    const isFordFlexBuyDeal = offerReduxSelectors.isFordFlexBuyDeal(state);
    const flexBuyPaymentGridToggle = offerReduxSelectors.getFlexBuyPaymentGridToggle(state);
    const paymentGridToggleEnabled = offerReduxSelectors.getPaymentGridToggle(state, offerType);
    const isFordFlexBuyGrid = isFordFlexBuyDeal && flexBuyPaymentGridToggle;
    const isRegularFinanceGrid = !isFordFlexBuyDeal && paymentGridToggleEnabled;
    const showPaymentGrid = isFordFlexBuyGrid || isRegularFinanceGrid;

    const vehicleData = getPdfVehicle(state);
    const paymentData = getCustomizablePdfPaymentPayload(state, offerType as Exclude<OfferType, typeof BALLOON>);
    const fuelSavingData = getFuelSavingsPrintData(state, isPrimaryScenario ? undefined : primaryState);
    const paymentGrid = showPaymentGrid ? offerReduxSelectors.getPaymentGridABPencilPrinting(state) : undefined;
    const isCarsActApplicable = dealerSelectors.isCarsActDisclosuresApplicable(state);

    return {
        vehicle: vehicleData,
        payment: paymentData,
        ...(paymentGrid && {
            paymentGridData: {
                ...paymentGrid,
                gridData: isCarsActApplicable ? paymentGrid.gridData : omitTotalOfPaymentsAndConsideration(paymentGrid.gridData),
                ...(gridSelection && {
                    selectedTermIndices: gridSelection.selectedTermIndices,
                    selectedDownPaymentIndices: gridSelection.selectedDownPaymentIndices
                })
            }
        }),
        isPrimaryScenario,
        tradesMap: getPdfDealTradesMap(state),
        ...(fuelSavingData && { fuelSavingData })
    };
};

export const getCustomizablePdfPayload = (
    state: RealState,
    selectedScopeIds?: string[],
    scopeGridSelections?: ScopeGridSelections
): CustomizablePdfPayload => {
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
    const primaryState = scopedStateSelectors.getScopeStateById(state, primaryScopeId) as StateTree;
    const allScopesOrder = scopedStateSelectors.getActiveScopesOrder(state);
    const scopesOrder = selectedScopeIds ? allScopesOrder.filter((id) => selectedScopeIds.includes(id)) : allScopesOrder;

    const isLeadOverrideSettingEnabled = dealerSelectors.getLeadRoutingOverrideSettingsEnabled(primaryState);
    const dealerIdOverride = isLeadOverrideSettingEnabled ? dealerSelectors.getLeadOverrideDealerId(primaryState) : undefined;
    const partnerId = dealerSelectors.getPartnerId(primaryState);
    const getDealerIdOverride = partnerId !== 'MMD' ? dealerSelectors.getPartnerDealerId(primaryState) : dealerIdOverride;
    const dealerLogo = getPrintingLogoUrl(primaryState);
    const isDealUrlEnabled = dealerSelectors.isEnableDealUrlForPrintMVEnabled(primaryState);
    const publishToShowroom = dealerSelectors.getIsTraditionalMode(primaryState);
    const isQuoteMode = quoteSelectors.getQuoteMode(primaryState);
    const dealXgId = offerReduxSelectors.getDealXgId(primaryState);
    const isDealUrlUnavailable = isQuoteMode || !dealXgId || !vehicleSelectors.getHasVehicle(primaryState);

    const deals = scopesOrder
        .map((scopeId) => {
            const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId);
            if (!scopeState) return undefined;
            const isPrimaryScenario = scopeId === primaryScopeId;
            return getDealForCustomizablePdf(
                scopeState as StateTree,
                isPrimaryScenario,
                primaryState,
                scopeGridSelections?.[scopeId]
            );
        })
        .filter(Boolean) as CustomizablePdfDeal[];
    const isDealNonEmpty = (deal: CustomizablePdfDeal): boolean =>
        !!deal.payment?.offerType || !!deal.vehicle?.make || !!deal.vehicle?.model || !!deal.vehicle?.year;
    const dealsToPrint = deals.filter(isDealNonEmpty);
    const coBuyerName = getCoBuyerName(primaryState);
    const payload: CustomizablePdfPayload = {
        deals: dealsToPrint,
        applicationName: 'Manager View' as const,
        dealer: getPdfDealer(primaryState),
        trades: getPdfTradeIns(primaryState),
        shopper: getPdfShopper(primaryState),
        ...(coBuyerName && { coBuyerName }),
        dealXgId,
        dealXgVersion: offerReduxSelectors.getDealXgVersion(primaryState) as string,
        pdfUpload: {
            dealRefId: offerSelectors.getDealRefId(primaryState),
            dealerIdOverride: getDealerIdOverride,
            partnerId: partnerId !== 'MMD' ? partnerId : undefined
        },
        dealerLogo,
        ...(publishToShowroom && dealXgId && { publishToShowroom })
    };

    if (isDealUrlEnabled && !isDealUrlUnavailable) {
        payload.dealUrl = getDealLink(primaryState);
    }

    const autoLeadId = getAutoLeadId(primaryState);

    if (autoLeadId) {
        payload.autoLeadId = autoLeadId;

        const vinDealerId = dealerSelectors.getVinSolutionsDealerId(primaryState);
        if (vinDealerId > 0) {
            payload.vinDealerId = vinDealerId;
        }

        const printedBy = getBridgeUserFullName(primaryState);
        if (printedBy) {
            payload.printedBy = printedBy;
        }
    }

    return payload;
};

export type PrintableScopeOfferCategory = 'cash' | 'finance' | 'lease' | 'balloon' | 'flexbuy';

export interface PrintableScopeSummary {
    scopeId: string;
    vehicleName: string;
    vehicleImageUrl: string;
    vin: string;
    stockNumber: string;
    scenarioType: string;
    paymentSummary: string;
    offerCategory: PrintableScopeOfferCategory;
}

export const getPrintableScopeSummaries = (realState: RealState): PrintableScopeSummary[] => {
    const scopesOrder = scopedStateSelectors.getActiveScopesOrder(realState);

    return scopesOrder
        .map((scopeId) => {
            const scopeState = scopedStateSelectors.getScopeStateById(realState, scopeId) as StateTree;
            const offerType = offerReduxSelectors.getCurrentOfferType(scopeState);
            const year = vehicleSelectors.getVehicleYear(scopeState);
            const make = vehicleSelectors.getVehicleMake(scopeState);
            const model = vehicleSelectors.getVehicleModel(scopeState);
            const trim = vehicleSelectors.getVehicleTrim(scopeState);
            const vehicleName = [year, make, model, trim].filter(Boolean).join(' ');
            const vehicleImageUrl = vehicleSelectors.getVehicleImageUrl(scopeState) || '';
            const vin = vehicleSelectors.getVehicleVin(scopeState) || '';
            const stockNumber = vehicleSelectors.getVehicleStockNumber(scopeState) || '';

            const scenarioType = isFlexBuyFinance(scopeState, offerType) ? 'Flex Buy' : capitalizeOfferType(offerType);
            const paymentSummary = getScopePaymentSummary(scopeState, offerType, 'print');

            const offerCategory: PrintableScopeOfferCategory = offerReduxSelectors.isFlexBuyPayment(scopeState)
                ? 'flexbuy'
                : ((offerType || '').toLowerCase() as PrintableScopeOfferCategory);

            return {
                scopeId,
                vehicleName,
                vehicleImageUrl,
                vin,
                stockNumber,
                scenarioType,
                paymentSummary,
                offerCategory
            };
        })
        .filter((summary) => !!summary.vehicleName || !!summary.scenarioType);
};

export const getAreAllScopesBalloonOrFlexBuy = (realState: RealState): boolean => {
    const scopesOrder = scopedStateSelectors.getActiveScopesOrder(realState);
    if (scopesOrder.length === 0) return false;

    return scopesOrder.every((scopeId) => {
        const scopeState = scopedStateSelectors.getScopeStateById(realState, scopeId) as StateTree;
        const offerType = offerReduxSelectors.getCurrentOfferType(scopeState);
        return offerReduxSelectors.isFlexBuyPayment(scopeState) || offerType === BALLOON;
    });
};

export const getGeneratePdfPayload = (state: RealState, type: string): GeneratePdfPayload => {
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
    const primaryState = scopedStateSelectors.getScopeStateById(state, primaryScopeId) as StateTree;

    const dealerLogo = getPrintingLogoUrl(primaryState);
    const isDealUrlEnabled = dealerSelectors.isEnableDealUrlForPrintMVEnabled(primaryState);
    const coBuyerName = getCoBuyerName(primaryState);
    const tradeEvaluationData = tradeInsSelectors.getTradeReconData(primaryState);
    const fuelSavingData = getFuelSavingPdfData(state);
    const tradeReconSettings = dealerSelectors.getTradeReconSettings(primaryState);
    const showTradePayoff = tradeReconSettings.showTradePayOff || false;
    const payload: GeneratePdfPayload = {
        applicationName: 'Manager View' as const,
        dealer: getPdfDealer(primaryState),
        shopper: getPdfShopper(primaryState),
        ...(coBuyerName && { coBuyerName }),
        dealXgId: offerReduxSelectors.getDealXgId(primaryState),
        dealXgVersion: offerReduxSelectors.getDealXgVersion(primaryState) as string,
        pdfUpload: {
            dealRefId: offerSelectors.getDealRefId(primaryState)
        },
        dealerLogo,
        tradeEvaluation: { trades: tradeEvaluationData, showTradePayoff },
        ...(fuelSavingData && { fuelSaving: fuelSavingData }),
        type
    };

    // The shopper page cannot render a deal without a selling vehicle, so the link is left out.
    if (isDealUrlEnabled && vehicleSelectors.getHasVehicle(primaryState)) {
        payload.dealUrl = getDealLink(primaryState);
    }

    return payload;
};
